import { useContext, useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Box, Checkbox, FormField, Label, LabeledControl, Select, TextButton } from '@zillow/constellation-10';
import { IconExternalOutline } from '@zillow/constellation-icons';

import PropertiesActions from '../../../../actions/PropertiesActions';
import actions from '../../../../containers/CreateListingPage/actions';
import { useForm } from '../../../../hooks/index';
import { useSetListingCriteria } from '../../../../hooks/queries/listingCriteria';
import { useMultiplexListingFlow } from '../../../../hooks/queries/multiplex';
import { AppSelectors } from '../../../../selectors/index';
import { useSelector } from '../../../../selectors/useSelector';
import { postingConstraints } from '../../../../utils/formValidation';
import wizardActions from '../../../Wizard/actions';

import { TRIGGER_LOCATIONS, TRIGGER_OBJECT } from '../../../../constants/ClickstreamConstants';
import { LC_MIN_CREDIT_SCORE_OPTIONS, NOT_APPLICABLE, TYPES_MAP } from '../../../../constants/ListingCriteriaConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import FinancialExpectationsCSEvents from '../../../../model/ClickstreamEvent/FinancialExpectationsCSEvents';
import Criterion from '../../../../model/ListingCriteria/Criterion';
import ListingCriteria from '../../../../model/ListingCriteria/index';
import WizardServerError from '../../../../model/WizardServerError';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';

import { WizardContext } from '../../../Wizard/context';
import WizardAlert from '../../../WizardAlert';

import type { ChangeEvent } from 'react';
import type { UserPropertyCriteriaSetRequestWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';
import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';

type MockEventProps = {
  target: {
    name: string;
    value: string | boolean;
    checked?: string | boolean;
  };
  persist?: () => void;
};

type SaveEvent = ChangeEvent<HTMLInputElement> | MockEventProps;

type PropTypes = {
  label?: string | React.ReactNode;
  creditScoreMax?: string;
};

type FormInputTypes = {
  criterion: string;
  negotiable: boolean;
};

// special value that triggers saving of all input fields
const SAVE_ALL_INPUTS = 'SAVE_ALL_INPUTS';

const criteriaType = TYPES_MAP.CREDIT_SCORE;

const inputMinCreditScoreName = 'criterion';
const inputNegotiableName = 'negotiable';

const hasValueChanged = (prevValueObject: FormInputTypes, currentValueObj: FormInputTypes) => {
  return (
    prevValueObject[inputMinCreditScoreName] !== currentValueObj[inputMinCreditScoreName] ||
    prevValueObject[inputNegotiableName] !== currentValueObj[inputNegotiableName]
  );
};

const MinCreditScoreFormField = ({ label = '', creditScoreMax = '' }: PropTypes) => {
  const dispatch = useDispatch();

  const { reducerWizardDispatch } = useContext(WizardContext);

  const {
    modularListingState: { isInModalState, isSaveComplete, saveOutsideComponent, serverErrorObj, shouldSaveOnBlur },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { isInMultiplexListingFlow } = useMultiplexListingFlow();

  const { currentCriteria, currentProperty, listingAlias, user } = useSelector((state) => {
    return {
      currentCriteria: state?.properties?.currentProperty?.listingCriteria?.criteria,
      currentProperty: state?.properties?.currentProperty,
      listingAlias: state?.properties?.currentProperty?.listingAlias,
      user: state?.user,
    };
  }, shallowEqual);

  const { initCriterionValue, initialNegotiableValue, targetCriteria } = useMemo(() => {
    const foundIndex = currentCriteria?.findIndex((obj: CriterionProps) => obj.type === criteriaType);
    const targetCriteria = currentCriteria?.[foundIndex];

    return {
      initCriterionValue: targetCriteria?.criterion,
      initialNegotiableValue: targetCriteria?.negotiable,
      targetCriteria,
    };
  }, [currentCriteria, criteriaType]);

  const prevValue = useRef<FormInputTypes | null>();

  const isScreenDesktop = useSelector((state) => AppSelectors.makeIsScreenDesktop(state));

  const { mutateAsync: updateListingCriteria } = useSetListingCriteria();

  const {
    inputs,
    errorObj,
    hasFieldError,
    handleChange: handleFormChange,
  } = useForm(
    {
      [inputMinCreditScoreName]: initCriterionValue,
      [inputNegotiableName]: initialNegotiableValue,
    },
    postingConstraints,
  );

  const { active } = currentProperty;
  const triggerLocation = active ? TRIGGER_LOCATIONS.POST_ACTIVATION : TRIGGER_LOCATIONS.POSTING_PATH;
  const triggerObject = isInModalState ? TRIGGER_OBJECT.REVIEW : TRIGGER_OBJECT.FINANCIAL_EXPECTATIONS_PAGE;

  const isOptionValid = (option: { value: string | number }) => {
    // always include "Not applicable" option regardless of max credit score
    if (option.value === 'Not applicable') {
      return true;
    }

    return option.value <= creditScoreMax;
  };

  const filteredLCMinCreditScoreOptions = useMemo(() => {
    return creditScoreMax === '' ? LC_MIN_CREDIT_SCORE_OPTIONS : LC_MIN_CREDIT_SCORE_OPTIONS.filter(isOptionValid);
  }, [creditScoreMax]);

  useEffect(() => {
    if (isSaveComplete) {
      // trigger all inputs to save
      save({
        target: {
          name: SAVE_ALL_INPUTS,
          value: '',
        },
      });
    }
  }, [isSaveComplete]);

  useEffect(() => {
    // @ts-expect-error
    updatePrevValueRef({
      ...inputs,
    });
  }, []);

  const clearServerError = () => {
    if (serverErrorObj.fieldName) {
      actions.removeServerError({
        reducerDispatch,
      });
    }
  };

  /**
   * criterion can not be an empty string when saving
   * set to NOT_APPLICABLE if empty
   */
  const normalizeCriterion = (dataArray: Array<CriterionProps>) => {
    if (dataArray.length === 0) {
      return dataArray;
    }

    const dataArrayCopy = dataArray.slice(0);

    if (dataArrayCopy[0]['criterion'] === '') {
      dataArrayCopy[0]['criterion'] = NOT_APPLICABLE;
    }

    return dataArrayCopy;
  };

  const save = (event: SaveEvent) => {
    if (isInMultiplexListingFlow) {
      // TODO: handle save for multiplex listing flow
      return;
    }

    // return if user must click on cta
    if (!shouldSaveOnBlur && !isSaveComplete) {
      return;
    }

    const { value = '', name: fieldName } = event.target;

    let isValueChanging = false;
    if (fieldName !== SAVE_ALL_INPUTS) {
      isValueChanging =
        !!prevValue.current &&
        // @ts-expect-error
        hasValueChanged(prevValue.current, {
          ...inputs,
          // use data from event
          [fieldName]: value,
        });
    } else {
      // @ts-expect-error
      isValueChanging = (prevValue.current && hasValueChanged(prevValue.current, inputs)) || false;
    }

    if (hasFieldError(fieldName, value)) {
      return;
    }

    if (shouldSaveOnBlur == false && isValueChanging === false && saveOutsideComponent === false) {
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    // saveOutsideComponent check must be above updateIsFetchingData to keep next and prev functionality
    if (saveOutsideComponent) {
      if (isValueChanging === false) {
        // still set fieldName
        actions.addFieldDataEntry({ reducerDispatch, entryObj: { fieldName: criteriaType } });
      } else {
        actions.addFieldDataEntry({
          reducerDispatch,
          entryObj: { fieldName: criteriaType, value: { ...targetCriteria, ...inputs } },
        });
      }

      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    // dynamically set the fieldName to handle all fields via save
    const criterionData = Criterion({
      ...targetCriteria,
      // updates
      ...inputs,
      isEnabled: true,
    });

    // use data from event to handle checkbox changes race condition
    if (fieldName === inputMinCreditScoreName) {
      criterionData.criterion = String(value);
    } else if (fieldName === inputNegotiableName) {
      criterionData.negotiable = Boolean(value);
    }

    const dataArray: Array<CriterionProps> = normalizeCriterion([criterionData]);

    updateListingCriteria({
      getParams: { listingAlias },
      postData: dataArray as Array<UserPropertyCriteriaSetRequestWebDto>,
    })
      .then(() => {
        // sync prev value
        updatePrevValueRef({
          [inputMinCreditScoreName]: criterionData[inputMinCreditScoreName],
          [inputNegotiableName]: !!criterionData[inputNegotiableName],
        });

        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled: true,
              criteria: [...currentCriteria, ...dataArray],
            }),
          }),
        );

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: { error: ErrorRMWProps }) => {
        const {
          error: { message },
        } = err;

        updateServerError(message, criteriaType);
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const updatePrevValueRef = (value: FormInputTypes) => {
    prevValue.current = {
      ...value,
    };
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const handleChange = (event: SaveEvent) => {
    const { value, name } = event.target;

    const mockEvent = {
      target: {
        name,
        value,
      },
    };

    clearServerError();
    handleFormChange(mockEvent);
    FinancialExpectationsCSEvents.MinimumCreditScoreCriteria({
      currentProperty,
      triggerLocation,
      triggerObject,
      user,
    });
  };

  const handleCheckboxChange = (event: SaveEvent) => {
    const { checked, name } = event.target;

    if (typeof checked === 'boolean') {
      const mockEvent: SaveEvent = {
        target: {
          name,
          value: checked,
        },
      };

      clearServerError();
      // @ts-expect-error
      handleFormChange(mockEvent);

      if (shouldSaveOnBlur) {
        save(mockEvent);
      }

      FinancialExpectationsCSEvents.CreditScoreNegotiableCheckbox({
        currentProperty,
        triggerLocation,
        triggerObject,
        user,
      });
    }
  };

  return (
    <>
      {serverErrorObj.fieldName === criteriaType && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <FormField
        data-testid="MinCreditScoreFormField"
        label={<Label required={false}>{label}</Label>}
        control={
          <Select
            size={isScreenDesktop ? 'lg' : 'md'}
            aria-label="Minimum credit score"
            name={inputMinCreditScoreName}
            value={inputs[inputMinCreditScoreName] as string}
            onChange={handleChange}
            onBlur={save}
            // @ts-expect-error
            error={!!errorObj[inputMinCreditScoreName]}
            required={false}
            data-eventid={FinancialExpectationsCSEvents.MinimumCreditScoreCriteria.data_event_id}
          >
            {filteredLCMinCreditScoreOptions.map(({ label, value }) => {
              return (
                <option key={value as string} value={value}>
                  {label}
                </option>
              );
            })}
          </Select>
        }
      />

      <FormField>
        <LabeledControl
          label={<Label>Credit score is negotiable</Label>}
          control={
            <Checkbox
              name={inputNegotiableName}
              checked={Boolean(inputs[inputNegotiableName])}
              onChange={handleCheckboxChange}
              data-eventid={FinancialExpectationsCSEvents.CreditScoreNegotiableCheckbox.data_event_id}
              data-testid="MinCreditScoreFormField-NegotiableCheckbox"
            />
          }
        />
      </FormField>

      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <TextButton
          asChild
          textStyle="body-sm"
          css={{ marginRight: '200' }}
          icon={<IconExternalOutline />}
          iconPosition="end"
        >
          <a
            href="https://www.experian.com/blogs/ask-experian/credit-education/score-basics/what-is-a-good-credit-score/"
            target="_blank"
            rel="noreferrer"
          >
            How Experian talks about credit scores
          </a>
        </TextButton>
      </Box>
    </>
  );
};

export default MinCreditScoreFormField;
